import React from 'react';
import { Tooltip } from '../../atoms/Tooltip';
import './FeatureCard.css';

export interface FeatureCardFeature {
  /** Feature text */
  text: string;
  /** Whether the feature is included (true) or excluded (false). Default: true */
  included?: boolean;
  /** Optional tooltip with more details */
  tooltip?: string;
}

export interface FeatureCardProps {
  /** Card title */
  title: string;
  /** Optional subtitle */
  subtitle?: string;
  /** Price display - accepts PriceDisplay component or custom content */
  price?: React.ReactNode;
  /** List of features */
  features: FeatureCardFeature[];
  /** Action buttons/links at the bottom */
  actions?: React.ReactNode;
  /** Highlights the card (e.g., "Most Popular") */
  highlighted?: boolean;
  /** Disables the card */
  disabled?: boolean;
  /** Badge text for highlighted cards (e.g., "Most Popular", "Best Value") */
  badge?: string;
  /** Custom CSS class */
  className?: string;
}

export const FeatureCard: React.FC<FeatureCardProps> = ({
  title,
  subtitle,
  price,
  features,
  actions,
  highlighted = false,
  disabled = false,
  badge,
  className = '',
}) => {
  const classes = [
    'hi-ui-feature-card',
    highlighted ? 'hi-ui-feature-card--highlighted' : '',
    disabled ? 'hi-ui-feature-card--disabled' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <article className={classes} aria-disabled={disabled || undefined}>
      {badge && <div className="hi-ui-feature-card__badge">{badge}</div>}

      <header className="hi-ui-feature-card__header">
        <h3 className="hi-ui-feature-card__title">{title}</h3>
        {subtitle && <p className="hi-ui-feature-card__subtitle">{subtitle}</p>}
        {price && <div className="hi-ui-feature-card__price">{price}</div>}
      </header>

      <ul className="hi-ui-feature-card__features" role="list">
        {features.map((feature, index) => {
          const isIncluded = feature.included !== false;

          const featureContent = (
            <li
              key={index}
              className={`hi-ui-feature-card__feature ${
                isIncluded
                  ? 'hi-ui-feature-card__feature--included'
                  : 'hi-ui-feature-card__feature--excluded'
              }`}
            >
              <span className="hi-ui-feature-card__feature-icon" aria-hidden="true">
                {isIncluded ? (
                  <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                ) : (
                  <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path
                      fillRule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
              </span>
              <span className="hi-ui-feature-card__feature-text">
                {feature.text}
                {feature.tooltip && (
                  <span className="hi-ui-feature-card__feature-info" aria-hidden="true">
                    <svg viewBox="0 0 20 20" fill="currentColor">
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </span>
                )}
              </span>
              <span className="sr-only">
                {isIncluded ? 'Included:' : 'Not included:'} {feature.text}
              </span>
            </li>
          );

          if (feature.tooltip) {
            return (
              <Tooltip key={index} content={feature.tooltip} position="top">
                {featureContent}
              </Tooltip>
            );
          }

          return featureContent;
        })}
      </ul>

      {actions && <footer className="hi-ui-feature-card__actions">{actions}</footer>}
    </article>
  );
};
